import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { apps, getApp } from "@/lib/apps";
import { introDelay } from "@/lib/lenis";
import { EASE, Eyebrow, MaskLines, Reveal } from "@/components/motion";
import { StatusChip } from "@/components/AppCard";
import { StoreBadges } from "@/components/StoreBadges";
import { TiltFrame } from "@/components/TiltFrame";
import { Waitlist } from "@/components/Waitlist";
import { AppScreenshots } from "@/components/AppScreenshots";
import { useAppStatus, useStoreLinks } from "@/lib/storeLinks";
import NotFound from "@/pages/NotFound";

const MetaItem = ({ label, children, testid }) => (
  <div data-testid={testid} className="border-t border-white/10 pt-5">
    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40">{label}</p>
    <div className="mt-3 text-white/85">{children}</div>
  </div>
);

export default function AppDetail() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [delay] = useState(introDelay);
  const app = getApp(slug) || apps[0];
  const status = useAppStatus(app);
  const { google, apple } = useStoreLinks(app.slug);
  if (!getApp(slug)) return <NotFound />;
  const next = apps[(apps.indexOf(app) + 1) % apps.length];
  const fade = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1, ease: EASE, delay: delay + d } });

  return (
    <div key={slug} data-testid={`app-detail-${slug}`} style={{ "--accent": app.accent }}>
      <section className="relative overflow-hidden pb-20 pt-32 sm:pt-40">
        <div className="pointer-events-none absolute -right-[15%] -top-[25%] h-[70vw] w-[70vw] rounded-full" style={{ background: `radial-gradient(circle, ${app.accent}30, ${app.accent2}12 40%, transparent 65%)` }} />
        <div className="hero-grid pointer-events-none absolute inset-0" />
        <div className="container-x relative grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div {...fade(0)}>
              <Link to="/#apps" data-testid="app-detail-back-link" className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/50 transition-colors hover:text-white">
                <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-500 group-hover:-translate-x-1" />
                {t.detail.back}
              </Link>
            </motion.div>
            <motion.div {...fade(0.05)} className="mt-10"><Eyebrow>{app.category[lang]}</Eyebrow></motion.div>
            <h1 key={lang} data-testid="app-detail-name" className="mt-6 whitespace-nowrap font-display text-[clamp(3.25rem,7vw,8rem)] font-extrabold leading-[0.95] tracking-[-0.05em]">
              <MaskLines lines={[app.name]} delay={delay + 0.1} />
            </h1>
            <motion.p {...fade(0.4)} data-testid="app-detail-tagline" className="mt-6 font-display text-2xl font-semibold tracking-tight sm:text-3xl" style={{ color: app.accent }}>
              {app.tagline[lang]}
            </motion.p>
            <motion.p {...fade(0.5)} className="mt-5 max-w-xl text-base leading-relaxed text-white/65 sm:text-lg">{app.short[lang]}</motion.p>
            <motion.div {...fade(0.6)} className="mt-10"><StoreBadges app={app} testPrefix="app-detail" /></motion.div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.85, rotate: -6 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 1.5, ease: EASE, delay: delay + 0.15 }} className="lg:col-span-5">
            <TiltFrame className="relative mx-auto w-[72vw] max-w-[440px]">
              <div className="absolute -inset-[15%] rounded-full blur-3xl" style={{ background: `radial-gradient(circle, ${app.accent}66, transparent 65%)`, transform: "translateZ(-60px)" }} />
              <div className="relative aspect-square overflow-hidden rounded-[26%] shadow-[0_50px_120px_-30px_rgba(0,0,0,0.9)] ring-1 ring-white/15" style={{ transform: "translateZ(40px)" }}>
                <img src={app.logo} alt={`${app.name} logo`} data-testid="app-detail-logo" className="h-full w-full object-cover" style={{ transform: `scale(${app.logoScale})` }} />
              </div>
            </TiltFrame>
          </motion.div>
        </div>
      </section>

      <section className="container-x grid gap-8 pb-20 sm:grid-cols-2 lg:grid-cols-4">
        <MetaItem label={t.detail.status} testid="app-detail-status"><StatusChip status={status} /></MetaItem>
        <MetaItem label={t.detail.platforms}>iOS · Android</MetaItem>
        <MetaItem label={t.detail.category}>{app.category[lang]}</MetaItem>
        <MetaItem label={t.detail.privacy}>
          <Link to={`/apps/${app.slug}/privacy`} data-testid="app-detail-privacy-link" className="link-underline inline-flex items-center gap-1">
            {app.name} — {t.detail.privacy} <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </MetaItem>
      </section>

      <AppScreenshots app={app} />

      <section className="container-x py-20 sm:py-28">
        <div className="grid items-end gap-8 lg:grid-cols-12">
          <h2 key={lang} className="section-title font-display font-bold lg:col-span-6"><MaskLines inView lines={[t.detail.features]} /></h2>
          <Reveal as="p" data-testid="app-detail-intro" className="text-base leading-relaxed text-white/65 sm:text-lg lg:col-span-6">{app.intro[lang]}</Reveal>
        </div>
        <div className="mt-14 grid gap-px overflow-hidden rounded-[32px] border border-white/10 bg-white/10 sm:grid-cols-2">
          {app.features.map((feat, i) => (
            <Reveal key={i} delay={(i % 2) * 0.07} data-testid={`app-feature-${i}`} className="group relative bg-[#05070f] p-8 transition-colors duration-500 hover:bg-[#0a0f1d] sm:p-12">
              <div className="flex items-start justify-between">
                <span className="grid h-14 w-14 place-items-center rounded-2xl border border-white/10 transition-[transform,background-color,color] duration-500 group-hover:-rotate-6 group-hover:bg-[var(--accent)] group-hover:text-[#05070f]" style={{ color: app.accent }}>
                  <feat.icon className="h-6 w-6" />
                </span>
                <span className="font-mono text-xs text-white/30">0{i + 1}</span>
              </div>
              <h3 className="mt-10 font-display text-2xl font-semibold tracking-tight sm:text-3xl">{feat[lang].t}</h3>
              <p className="mt-3 max-w-sm text-white/60">{feat[lang].d}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-16" data-testid="app-detail-highlights">
          <p className="eyebrow">{app.highlightsTitle[lang]}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {app.highlights[lang].map((h) => (
              <span key={h} className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/80 transition-[border-color,color,transform] duration-500 hover:-translate-y-0.5 hover:border-[var(--accent)] hover:text-white">{h}</span>
            ))}
          </div>
        </Reveal>
        {!(google && apple) && <Waitlist app={app} />}
      </section>

      <Link to={`/apps/${next.slug}`} data-testid="app-detail-next-link" className="group block border-t border-white/10">
        <div className="container-x flex items-center justify-between gap-4 py-16 sm:gap-8 sm:py-24">
          <div className="min-w-0">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">{t.detail.next}</p>
            <p className="mt-4 font-display text-[9vw] font-extrabold leading-none tracking-[-0.05em] transition-transform duration-700 ease-[cubic-bezier(.16,1,.3,1)] group-hover:translate-x-4 sm:text-8xl lg:text-9xl">{next.name}</p>
          </div>
          <div className="w-16 shrink-0 overflow-hidden rounded-[26%] ring-1 ring-white/15 transition-transform duration-700 ease-[cubic-bezier(.16,1,.3,1)] group-hover:rotate-6 group-hover:scale-110 sm:w-40">
            <img src={next.logo} alt={`${next.name} logo`} className="aspect-square w-full object-cover" style={{ transform: `scale(${next.logoScale})` }} />
          </div>
        </div>
      </Link>
    </div>
  );
}
